import React, { useMemo } from "react";
import styles from "../PostCallFeature/PostCallFeature.module.css";
import TitleBar from "../../TitleBar/TitleBar";

const getTitleBits = (heading = [], headingPlain) => {
  const first = heading?.[0];
  if (first?.children?.length) {
    const children = first.children;
    const normal =
      children.find((c) => (c.marks || []).length === 0)?.text || "";
    const bold =
      children.find((c) => (c.marks || []).includes("strong"))?.text || "";
    if (normal || bold) return { subTitle: normal, spanText: bold };
  }
  if (headingPlain) {
    const [line1 = "", line2 = ""] = headingPlain
      .split("\n")
      .map((s) => s.trim());
    const parts = line1.split(" ");
    const spanText = parts.pop() || "";
    const subTitle = parts.length ? parts.join(" ") + " " : "";
    return { subTitle, spanText, title: line2 || "Post-Call Features" };
  }
  return {
    subTitle: "Additional ",
    spanText: "Features",
    title: "Post-Call Features",
  };
};

const pickByKind = (cards = [], kind) => cards.find((c) => c.kind === kind);

const SafeImg = ({ src, alt, className }) =>
  src ? <img src={src} alt={alt || ""} className={className} /> : null;

const WaveBars = ({ count = 0, className }) => (
  <div className={className}>
    {[...Array(Math.max(0, count))].map((_, i) => (
      <span key={i} style={{ animationDelay: `${i * 0.1}s` }} />
    ))}
  </div>
);

const Concentric = ({ rings = 0 }) => (
  <>
    {[...Array(Math.max(0, rings || 0))].map((_, i) => (
      <div
        key={i}
        className={styles.circle}
        style={{ animationDelay: `${i * 1}s` }}
      />
    ))}
  </>
);

const Tooltips = ({ tips = [] }) => (
  <>
    {tips.map((t, i) => (
      <div
        key={i}
        className={`${styles.tooltip} ${styles[`tooltip${i + 1}`] || ""}`}
      >
        {t}
      </div>
    ))}
  </>
);

const ChatDemo = ({ lines = [] }) => (
  <div className={styles.chatDetails}>
    {lines.map((l, i) => (
      <p key={i}>
        <strong>{l.role}:</strong> {l.text}
      </p>
    ))}
  </div>
);

const PostCallFeature = ({ PostCallFeaturedata }) => {
  const { subTitle, spanText, title } = useMemo(
    () => ({
      ...getTitleBits(
        PostCallFeaturedata?.heading,
        PostCallFeaturedata?.headingPlain
      ),
      title:
        PostCallFeaturedata?.heading?.[1]?.children?.[0]?.text ||
        "Post-Call Features",
    }),
    [PostCallFeaturedata]
  );

  const cards = PostCallFeaturedata?.cards || [];

  const voiceCapture = useMemo(
    () => pickByKind(cards, "voiceCapture"),
    [cards]
  );
  const transcription = useMemo(
    () => pickByKind(cards, "transcription"),
    [cards]
  );
  const followUp = useMemo(() => pickByKind(cards, "followUp"), [cards]);
  const recording = useMemo(() => pickByKind(cards, "recording"), [cards]);

  return (
    <div className={styles.containerBox}>
      <div className={styles.TitleSection}>
        <TitleBar blocks={PostCallFeaturedata?.heading} />
      </div>
      <div className={styles.container}>
        <div className={styles.leftCard}>
          <h2>{voiceCapture?.title || "Seamless Voice Capture"}</h2>
          <p>{voiceCapture?.desc || ""}</p>

          <div className={styles.waveContainer}>
            <div className={styles.centerLogo}>
              <SafeImg
                src={voiceCapture?.img?.src}
                alt={voiceCapture?.img?.alt || "center-logo"}
              />
            </div>
            <Tooltips tips={voiceCapture?.tooltips || []} />
            <Concentric rings={voiceCapture?.rings ?? 5} />
          </div>
        </div>
        <div className={styles.rightCard}>
          <h2>{transcription?.title || "Call Transcription"}</h2>
          <p>{transcription?.desc || ""}</p>

          <div className={styles.chatBox}>
            <WaveBars
              count={transcription?.bars ?? 20}
              className={styles.waveform}
            />
            <ChatDemo lines={transcription?.chatDemo || []} />
          </div>
        </div>
      </div>
      <div className={styles.container2}>
        <div className={styles.rightCard}>
          <h2>{followUp?.title || "Follow-Up Reminders"}</h2>
          <p>{followUp?.desc || ""}</p>

          <div className={styles.bellDiv}>
            <SafeImg
              src={followUp?.img?.src}
              alt={followUp?.img?.alt || "follow-up-icon"}
            />
          </div>
        </div>
        <div className={styles.leftCard}>
          <h2>{recording?.title || "Call Recording"}</h2>
          <p>{recording?.desc || ""}</p>

          <div className={styles.waveContainer}>
            <div className={styles.callTransDiv}>
              <div className={styles.callTranscription}>
                <SafeImg
                  src={recording?.img?.src}
                  alt={recording?.img?.alt || "call-transcription-icon"}
                />
              </div>
              <WaveBars
                count={recording?.bars ?? 30}
                className={styles.waveform2}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PostCallFeature;
